/**
 * Sound Service Module
 * Exports sound management functionality for combat audio
 * @module services/sound
 */

import { SoundManager } from './sound-manager';
import { CombatAudioController } from './combat-audio-controller';
import type { SoundManagerConfig } from './types';
import type { CombatAudioConfig } from './combat-audio-types';

// Singleton instances
let soundManagerInstance: SoundManager | null = null;
let audioControllerInstance: CombatAudioController | null = null;

/**
 * Get or create the singleton SoundManager instance
 * @param config - Optional configuration (only used on first call)
 */
export function getSoundManager(config?: Partial<SoundManagerConfig>): SoundManager {
  if (!soundManagerInstance) {
    soundManagerInstance = new SoundManager(config);
  }
  return soundManagerInstance;
}

/**
 * Reset the SoundManager singleton instance (mainly for testing)
 */
export function resetSoundManager(): void {
  if (soundManagerInstance) {
    soundManagerInstance.cleanup();
    soundManagerInstance = null;
  }
}

/**
 * Get or create the singleton CombatAudioController instance
 * @param config - Optional configuration (only used on first call)
 */
export function getCombatAudioController(
  config?: Partial<CombatAudioConfig>
): CombatAudioController {
  if (!audioControllerInstance) {
    audioControllerInstance = new CombatAudioController(config);
  }
  return audioControllerInstance;
}

/**
 * Reset the CombatAudioController singleton instance
 */
export function resetCombatAudioController(): void {
  if (audioControllerInstance) {
    audioControllerInstance.cleanup();
    audioControllerInstance = null;
  }
}

/**
 * Reset all sound service singletons
 */
export function resetAllSoundServices(): void {
  resetCombatAudioController();
  resetSoundManager();
}

// Re-export classes
export { SoundManager } from './sound-manager';
export { CombatAudioController } from './combat-audio-controller';
export type { CreatureAudioData } from './combat-audio-controller';

// Re-export utilities
export { gsUrlToHttps, isGsUrl } from './url-converter';
export { SHARED_SOUND_URLS } from './shared-sounds';
export type { SharedSoundKey } from './shared-sounds';

// Re-export types
export type {
  SoundType,
  CreatureSoundType,
  SharedSoundType,
  CreatureSoundUrls,
  SoundManagerConfig,
} from './types';

export type { CombatAudioConfig, AudioRelevantEventType } from './combat-audio-types';
export { DEFAULT_AUDIO_CONFIG } from './combat-audio-types';
